import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getJournals, createJournal, updateJournal, deleteJournal, searchJournals, extractErrorMessage } from '../services/api';
import { FaPlus, FaEdit, FaTrash, FaBook, FaChevronDown, FaChevronUp, FaSearch } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import '../styles/Journals.css';

function Journals({ user }) {
  const toast = useToast();
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingJournal, setEditingJournal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [moodFilter, setMoodFilter] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    mood: '',
    tags: '',
    is_private: false
  });

  useEffect(() => {
    fetchJournals();
  }, [moodFilter]);

  const fetchJournals = async () => {
    try {
      setLoading(true);
      const params = moodFilter !== 'all' ? { mood: moodFilter } : {};
      const data = await getJournals(params);
      setJournals(data.journals || data);
    } catch (error) {
      console.error('Error fetching journals:', error);
      toast.error(extractErrorMessage(error, 'Journal-Einträge konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchJournals();
      return;
    }
    try {
      setLoading(true);
      const data = await searchJournals(searchQuery);
      setJournals(data.journals || data);
    } catch (error) {
      console.error('Error searching journals:', error);
      toast.error(extractErrorMessage(error, 'Suche fehlgeschlagen'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : []
      };
      if (editingJournal) {
        await updateJournal(editingJournal.id, payload);
      } else {
        await createJournal(payload);
      }
      resetForm();
      setShowModal(false);
      toast.success(editingJournal ? 'Eintrag gespeichert' : 'Eintrag erstellt');
      fetchJournals();
    } catch (error) {
      console.error('Error saving journal:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Speichern des Eintrags'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (journal) => {
    setEditingJournal(journal);
    setFormData({
      title: journal.title || '',
      content: journal.content || '',
      mood: journal.mood || '',
      tags: Array.isArray(journal.tags) ? journal.tags.join(', ') : (journal.tags || ''),
      is_private: journal.is_private || false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try {
      await deleteJournal(id);
      toast.success('Eintrag gelöscht');
      fetchJournals();
    } catch (error) {
      console.error('Error deleting journal:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen des Eintrags'));
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const resetForm = () => {
    setEditingJournal(null);
    setFormData({ title: '', content: '', mood: '', tags: '', is_private: false });
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getMoodEmoji = (mood) => {
    const moods = {
      happy: '😊',
      sad: '😢',
      anxious: '😰',
      calm: '😌',
      excited: '🤩',
      angry: '😠',
      grateful: '🙏',
      reflective: '🤔',
      submissive: '🧎',
      proud: '💪'
    };
    return moods[mood] || '📝';
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaBook /> Journal</h1>
          <button className="add-btn" onClick={openCreateModal}>
            <FaPlus /> Neuer Eintrag
          </button>
        </div>

        <div className="search-bar">
          <input
            type="text"
            placeholder="Einträge durchsuchen..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <button className="search-btn" onClick={handleSearch}>
            <FaSearch />
          </button>
        </div>

        <div className="filter-tabs">
          <button className={moodFilter === 'all' ? 'active' : ''} onClick={() => setMoodFilter('all')}>Alle</button>
          <button className={moodFilter === 'happy' ? 'active' : ''} onClick={() => setMoodFilter('happy')}>😊 Glücklich</button>
          <button className={moodFilter === 'sad' ? 'active' : ''} onClick={() => setMoodFilter('sad')}>😢 Traurig</button>
          <button className={moodFilter === 'calm' ? 'active' : ''} onClick={() => setMoodFilter('calm')}>😌 Ruhig</button>
          <button className={moodFilter === 'reflective' ? 'active' : ''} onClick={() => setMoodFilter('reflective')}>🤔 Nachdenklich</button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="journals-list">
            {journals.length > 0 ? (
              journals.map(journal => (
                <div key={journal.id} className={`journal-card ${expandedId === journal.id ? 'expanded' : ''}`}>
                  <div className="journal-header" onClick={() => toggleExpand(journal.id)}>
                    <div className="journal-title-row">
                      <span className="mood-emoji">{getMoodEmoji(journal.mood)}</span>
                      <h3 className="journal-title">{journal.title || 'Ohne Titel'}</h3>
                      {journal.is_private && <span className="private-badge">Privat</span>}
                    </div>
                    <div className="journal-header-right">
                      <span className="journal-date">
                        {new Date(journal.created_at || journal.timestamp).toLocaleDateString('de-DE', {
                          day: '2-digit', month: 'short', year: 'numeric'
                        })}
                      </span>
                      {expandedId === journal.id ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  </div>
                  {journal.tags && (
                    <div className="journal-tags">
                      {(Array.isArray(journal.tags) ? journal.tags : [journal.tags]).map((tag, i) => (
                        <span key={i} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                  {expandedId === journal.id && (
                    <div className="journal-body">
                      <div className="journal-content">
                        {journal.content}
                      </div>
                      <div className="journal-actions">
                        <button className="action-btn edit" onClick={(e) => { e.stopPropagation(); handleEdit(journal); }} title="Bearbeiten">
                          <FaEdit /> Bearbeiten
                        </button>
                        <button className="action-btn delete" onClick={(e) => { e.stopPropagation(); handleDelete(journal.id); }} title="Löschen">
                          <FaTrash /> Löschen
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-journals">Keine Einträge gefunden</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>{editingJournal ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <label>Titel</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({...formData, title: e.target.value})}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-group">
                  <label>Stimmung</label>
                  <select
                    value={formData.mood}
                    onChange={e => setFormData({...formData, mood: e.target.value})}
                  >
                    <option value="">-- Wählen --</option>
                    <option value="happy">😊 Glücklich</option>
                    <option value="sad">😢 Traurig</option>
                    <option value="anxious">😰 Ängstlich</option>
                    <option value="calm">😌 Ruhig</option>
                    <option value="excited">🤩 Aufgeregt</option>
                    <option value="angry">😠 Wütend</option>
                    <option value="grateful">🙏 Dankbar</option>
                    <option value="reflective">🤔 Nachdenklich</option>
                    <option value="submissive">🧎 Unterwürfig</option>
                    <option value="proud">💪 Stolz</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Inhalt *</label>
                  <textarea
                    className="journal-textarea"
                    value={formData.content}
                    onChange={e => setFormData({...formData, content: e.target.value})}
                    required
                    placeholder="Schreibe deinen Eintrag..."
                  />
                </div>
                <div className="form-group">
                  <label>Tags (kommagetrennt)</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={e => setFormData({...formData, tags: e.target.value})}
                    placeholder="z.B. reflexion, training, szene"
                  />
                </div>
                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_private}
                      onChange={e => setFormData({...formData, is_private: e.target.checked})}
                    />
                    Privat (nur für mich sichtbar)
                  </label>
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>
                    {saving ? 'Speichert...' : (editingJournal ? 'Speichern' : 'Erstellen')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Journals;
